import { ArrowUpCircle, SkipForward, SlidersHorizontal } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchJson, sendJson } from "../../api/rest";
import {
	QueueStatusChip,
	summariseReason,
	TIER_ORDER,
	SerpClassChip,
	TierChip,
	tierInfo,
} from "../../components/flows/seo";
import {
	btn,
	Empty,
	ErrorBanner,
	fmtDateTime,
	fmtNumber,
	fmtRelative,
	input,
	Panel,
	Spinner,
} from "../../components/flows/ui";
import type { MaskedSetting, SettingsResponse } from "../../types/flows";
import type { SeoQueueList, SeoQueueRow, SeoStats } from "../../types/seo";

const STATUSES = ["queued", "claimed", "skipped", "done", "expired", ""];

const STATUS_LABELS: Record<string, string> = {
	"": "Any status",
	queued: "Queued",
	claimed: "Claimed",
	skipped: "Skipped",
	done: "Done",
	expired: "Expired",
};

const SETTING_GROUPS = ["seo", "dataforseo"];

const TierCounters = ({ stats }: { stats: SeoStats }) => {
	const byTier = new Map(stats.tiers.map((row) => [row.tier, row]));
	return (
		<div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
			{TIER_ORDER.map((tier) => {
				const info = tierInfo(tier);
				const row = byTier.get(tier);
				return (
					<div
						key={tier}
						title={info.blurb}
						className="rounded-xl border border-slate-200 bg-white p-3"
					>
						<p className="text-[12px] font-semibold text-slate-700">
							{info.label}
						</p>
						<p className="mt-2 text-[22px] font-bold leading-none text-slate-800 tabular-nums">
							{fmtNumber(row?.queued ?? 0)}
						</p>
						<p className="text-[11px] text-slate-400">queued</p>
						<p className="mt-2 text-[11px] text-slate-500 tabular-nums">
							{fmtNumber(row?.claimed ?? 0)} claimed ·{" "}
							{fmtNumber(row?.done7d ?? 0)} done in 7 d
						</p>
					</div>
				);
			})}
		</div>
	);
};

const FocusPanel = ({ stats }: { stats: SeoStats }) => {
	const { size, cutLineImpressions, version0Remaining } = stats.focus;
	const written = Math.max(0, size - version0Remaining);
	const share = size > 0 ? Math.round((written / size) * 100) : 0;

	return (
		<Panel
			title="The focus set"
			hint="the top pages by 28-day impressions, rebuilt weekly, membership sticky for 56 days"
		>
			{size === 0 ? (
				<Empty>
					The focus set is empty. It is rebuilt on Mondays by seo_queue_refill
					in backend-rust from product_position_daily, which track_positions
					fills from Search Console — until both have run there is no demand
					data to rank.
				</Empty>
			) : (
				<div className="space-y-4">
					<div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
						<div>
							<p className="text-[11px] uppercase tracking-wide text-slate-400">
								Members
							</p>
							<p className="text-[22px] font-bold text-slate-800 tabular-nums">
								{fmtNumber(size)}
							</p>
						</div>
						<div>
							<p className="text-[11px] uppercase tracking-wide text-slate-400">
								Impressions at the cut line
							</p>
							<p className="text-[22px] font-bold text-slate-800 tabular-nums">
								{fmtNumber(cutLineImpressions)}
							</p>
							<p className="text-[11px] text-slate-400">
								what the last page in the set earned in 28 days — how deep the
								demand goes, and whether the set is the right size
							</p>
						</div>
						<div>
							<p className="text-[11px] uppercase tracking-wide text-slate-400">
								Never optimised
							</p>
							<p className="text-[22px] font-bold text-slate-800 tabular-nums">
								{fmtNumber(version0Remaining)}
							</p>
							<p className="text-[11px] text-slate-400">
								still at version 0 — what the focus tier has left to do
							</p>
						</div>
					</div>

					<div>
						<div className="flex items-baseline justify-between text-[12px] text-slate-500">
							<span>First pass</span>
							<span className="tabular-nums">
								{fmtNumber(written)} of {fmtNumber(size)} · {share} %
							</span>
						</div>
						<div className="mt-1.5 h-2.5 overflow-hidden rounded-full bg-slate-100">
							<div
								className="h-full rounded-full bg-primary transition-all"
								style={{ width: `${share}%` }}
							/>
						</div>
					</div>
				</div>
			)}
		</Panel>
	);
};

const OutcomesPanel = ({ stats }: { stats: SeoStats }) => {
	const rows = stats.outcomes;
	const totals = rows.reduce(
		(acc, row) => ({
			improved: acc.improved + row.improved,
			flat: acc.flat + row.flat,
			regressed: acc.regressed + row.regressed,
			unmeasurable: acc.unmeasurable + row.unmeasurable,
		}),
		{ improved: 0, flat: 0, regressed: 0, unmeasurable: 0 },
	);

	return (
		<Panel
			title="How the writes did"
			hint="every version written in the last 90 days, by the tier that queued it"
		>
			{rows.length === 0 ? (
				<Empty>
					Nothing has been measured yet. A version is compared against its own
					before-metrics 28 days after it was written, so the first outcomes
					appear about a month after the first writes.
				</Empty>
			) : (
				<div className="overflow-x-auto">
					<table className="w-full text-left text-[13px]">
						<thead>
							<tr className="border-b border-slate-200 text-slate-500">
								<th className="pb-2 pr-3 font-medium">Tier</th>
								<th className="pb-2 pr-3 text-right font-medium">Improved</th>
								<th className="pb-2 pr-3 text-right font-medium">Flat</th>
								<th className="pb-2 pr-3 text-right font-medium">Regressed</th>
								<th className="pb-2 text-right font-medium">
									Too little traffic
								</th>
							</tr>
						</thead>
						<tbody>
							{rows.map((row) => (
								<tr
									key={row.tier}
									className="border-b border-slate-100 last:border-0"
								>
									<td className="py-2 pr-3">
										<TierChip tier={row.tier} />
									</td>
									<td className="py-2 pr-3 text-right tabular-nums text-emerald-700">
										{fmtNumber(row.improved)}
									</td>
									<td className="py-2 pr-3 text-right tabular-nums text-slate-500">
										{fmtNumber(row.flat)}
									</td>
									<td className="py-2 pr-3 text-right">
										{row.regressed > 0 ? (
											<span
												title="Position worse by at least 2.0, or clicks down 40 % at steady impressions. Each one is queued again as a regression and can be rolled back from the product's SEO history."
												className="inline-block rounded-full bg-rose-100 px-2 py-0.5 text-[12px] font-bold tabular-nums text-rose-800"
											>
												{fmtNumber(row.regressed)}
											</span>
										) : (
											<span className="tabular-nums text-slate-300">0</span>
										)}
									</td>
									<td className="py-2 text-right tabular-nums text-slate-400">
										{fmtNumber(row.unmeasurable)}
									</td>
								</tr>
							))}
							<tr className="text-slate-600">
								<td className="pt-2 pr-3 text-[12px] font-semibold">
									All tiers
								</td>
								<td className="pt-2 pr-3 text-right font-semibold tabular-nums">
									{fmtNumber(totals.improved)}
								</td>
								<td className="pt-2 pr-3 text-right font-semibold tabular-nums">
									{fmtNumber(totals.flat)}
								</td>
								<td className="pt-2 pr-3 text-right font-semibold tabular-nums">
									{fmtNumber(totals.regressed)}
								</td>
								<td className="pt-2 text-right font-semibold tabular-nums">
									{fmtNumber(totals.unmeasurable)}
								</td>
							</tr>
						</tbody>
					</table>

					{totals.regressed > 0 && (
						<p className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-[12px] leading-relaxed text-rose-800">
							{fmtNumber(totals.regressed)} version
							{totals.regressed === 1 ? " has" : "s have"} come back worse than
							what they replaced. That is the number that stops a rollout: turn
							the tier's daily budget down in Settings and roll the affected
							products back from their SEO history before writing more.
						</p>
					)}
				</div>
			)}
		</Panel>
	);
};

const DialsPanel = ({ settings }: { settings: MaskedSetting[] | null }) => (
	<Panel
		title="Budgets, cooldown and thresholds"
		hint="read-only here — one save path, and it is Settings"
		action={
			<Link to="/flows/settings" className={btn.ghost}>
				<SlidersHorizontal className="h-4 w-4" /> Open Settings
			</Link>
		}
	>
		{!settings || settings.length === 0 ? (
			<Empty>
				The SEO settings group has not loaded. The jobs fall back to their
				defaults until a value is saved in Settings.
			</Empty>
		) : (
			<div className="grid grid-cols-2 gap-x-6 gap-y-2 md:grid-cols-3 xl:grid-cols-4">
				{settings.map((setting) => (
					<div
						key={setting.key}
						title={setting.hint}
						className="flex items-baseline justify-between gap-2 border-b border-slate-100 py-1.5"
					>
						<span className="truncate text-[12px] text-slate-500">
							{setting.label}
						</span>
						<span className="shrink-0 font-mono text-[12px] tabular-nums text-slate-700">
							{setting.secret
								? setting.isSet
									? `set · …${setting.last4 ?? ""}`
									: "not set"
								: (setting.value ?? setting.placeholder ?? "—")}
							{!setting.secret && setting.value === null && (
								<span className="ml-1 text-[10px] font-sans text-slate-400">
									default
								</span>
							)}
						</span>
					</div>
				))}
			</div>
		)}
	</Panel>
);

const ReasonCell = ({ row }: { row: SeoQueueRow }) => {
	const parts = summariseReason(row.tier, row.reason);
	return (
		<span className="text-[12px] leading-relaxed text-slate-600">
			{parts.join(" · ")}
		</span>
	);
};

const FlowsSeoQueue = () => {
	const [stats, setStats] = useState<SeoStats | null>(null);
	const [settings, setSettings] = useState<MaskedSetting[] | null>(null);
	const [data, setData] = useState<SeoQueueList | null>(null);
	const [tier, setTier] = useState("");
	const [status, setStatus] = useState("queued");
	const [page, setPage] = useState(1);
	const [loading, setLoading] = useState(true);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const loadStats = useCallback(async () => {
		try {
			setStats(await fetchJson<SeoStats>("/flows/seo/stats"));
		} catch (e) {
			setError(String(e));
		}
	}, []);

	const loadQueue = useCallback(async () => {
		setError(null);
		try {
			const query = new URLSearchParams({
				page: String(page),
				pageSize: "50",
			});
			if (tier) query.set("tier", tier);
			if (status) query.set("status", status);
			setData(await fetchJson<SeoQueueList>(`/flows/seo/queue?${query}`));
		} catch (e) {
			setError(String(e));
		} finally {
			setLoading(false);
		}
	}, [page, tier, status]);

	useEffect(() => {
		loadStats();
		fetchJson<SettingsResponse>("/flows/settings")
			.then((response) =>
				setSettings(
					response.settings.filter((s) => SETTING_GROUPS.includes(s.group)),
				),
			)
			.catch(() => {
				setSettings([]);
			});
	}, [loadStats]);

	useEffect(() => {
		loadQueue();
	}, [loadQueue]);

	const setRowStatus = async (row: SeoQueueRow, next: "skipped" | "queued") => {
		setBusy(true);
		setError(null);
		try {
			const result = await sendJson<{ ok?: boolean; error?: string }>(
				`/flows/seo/queue/${row.id}`,
				"PATCH",
				{ status: next },
			);
			if (result.error) {
				setError(result.error);
				return;
			}
			await Promise.all([loadQueue(), loadStats()]);
		} catch (e) {
			setError(String(e));
		} finally {
			setBusy(false);
		}
	};

	if (loading) return <Spinner />;

	const items = data?.items ?? [];
	const totalPages = data?.totalPages ?? 1;

	return (
		<div className="space-y-4">
			<ErrorBanner error={error} />

			{stats && <TierCounters stats={stats} />}
			{stats && <FocusPanel stats={stats} />}
			{stats && <OutcomesPanel stats={stats} />}
			<DialsPanel settings={settings} />

			<Panel
				title="The queue"
				hint={`${fmtNumber(data?.total ?? 0)} row${data?.total === 1 ? "" : "s"} match`}
				action={
					<div className="flex items-center gap-2">
						<select
							className={`${input} w-44`}
							value={tier}
							onChange={(event) => {
								setTier(event.target.value);
								setPage(1);
							}}
						>
							<option value="">Every tier</option>
							{TIER_ORDER.map((option) => (
								<option key={option} value={option}>
									{tierInfo(option).label}
								</option>
							))}
						</select>
						<select
							className={`${input} w-40`}
							value={status}
							onChange={(event) => {
								setStatus(event.target.value);
								setPage(1);
							}}
						>
							{STATUSES.map((option) => (
								<option key={option} value={option}>
									{STATUS_LABELS[option]}
								</option>
							))}
						</select>
					</div>
				}
			>
				{items.length === 0 ? (
					<Empty>
						Nothing matches. Rows arrive from track_positions (drops and
						regressions), seo_queue_refill (focus, striking distance, demand and
						cold) and from Optimize now on a product — all three in backend-rust
						or the product page, never from this screen.
					</Empty>
				) : (
					<>
						<div className="overflow-x-auto">
							<table className="w-full text-left text-[13px]">
								<thead>
									<tr className="border-b border-slate-200 text-slate-500">
										<th className="pb-2 pr-3 font-medium">Product</th>
										<th className="pb-2 pr-3 font-medium">Tier</th>
										<th className="pb-2 pr-3 font-medium">SERP</th>
										<th className="pb-2 pr-3 font-medium">Why</th>
										<th className="pb-2 pr-3 font-medium">Status</th>
										<th className="pb-2 pr-3 font-medium">Queued</th>
										<th className="pb-2 pr-3 font-medium">Claimed by</th>
										<th className="pb-2 font-medium" />
									</tr>
								</thead>
								<tbody>
									{items.map((row) => (
										<tr
											key={row.id}
											className="border-b border-slate-100 last:border-0"
										>
											<td className="py-2 pr-3">
												<Link
													to={`/products/${row.productId}`}
													className="font-medium text-primary"
													title={row.productName ?? undefined}
												>
													<span className="line-clamp-2 max-w-64">
														{row.productName ?? `Product ${row.productId}`}
													</span>
												</Link>
											</td>
											<td className="py-2 pr-3">
												<TierChip tier={row.tier} />
											</td>
											<td className="py-2 pr-3">
												<SerpClassChip serpClass={row.serpClass} />
											</td>
											<td className="max-w-96 py-2 pr-3">
												<ReasonCell row={row} />
											</td>
											<td className="py-2 pr-3">
												<QueueStatusChip status={row.status} />
											</td>
											<td
												className="py-2 pr-3 text-slate-500"
												title={fmtDateTime(row.queuedAt)}
											>
												{fmtRelative(row.queuedAt)}
											</td>
											<td className="py-2 pr-3">
												{row.claimedRunId ? (
													<Link
														to={`/flows/runs/${row.claimedRunId}`}
														className="text-primary"
														title={`Claimed ${fmtDateTime(row.claimedAt)}`}
													>
														run #{row.claimedRunId}
													</Link>
												) : (
													<span className="text-slate-300">—</span>
												)}
											</td>
											<td className="py-2 text-right">
												{row.status === "skipped" ? (
													<button
														type="button"
														disabled={busy}
														onClick={() => setRowStatus(row, "queued")}
														title="Put this row back in the queue for the next run that reads its tier."
														className={btn.ghost}
													>
														<ArrowUpCircle className="h-4 w-4" /> Promote
													</button>
												) : row.status === "queued" ||
													row.status === "claimed" ? (
													<button
														type="button"
														disabled={busy}
														onClick={() => setRowStatus(row, "skipped")}
														title="Set this row aside. It stays on file and can be promoted back."
														className={btn.ghost}
													>
														<SkipForward className="h-4 w-4" /> Skip
													</button>
												) : null}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>

						{totalPages > 1 && (
							<div className="mt-4 flex items-center justify-between text-[12px] text-slate-500">
								<span>
									Page {data?.page ?? page} of {totalPages}
								</span>
								<div className="flex gap-2">
									<button
										type="button"
										disabled={page <= 1}
										onClick={() => setPage(page - 1)}
										className={btn.ghost}
									>
										Previous
									</button>
									<button
										type="button"
										disabled={page >= totalPages}
										onClick={() => setPage(page + 1)}
										className={btn.ghost}
									>
										Next
									</button>
								</div>
							</div>
						)}
					</>
				)}
			</Panel>
		</div>
	);
};

export default FlowsSeoQueue;
